import { VideoElementRenderer, VideoRendererHost } from './video-renderer';

const ICE_GATHERING_TIMEOUT_MS: number = 3000;

// go2rtc's reply when the camera's codec (H.265, typically) is missing from the
// browser's offer. A WebView without HEVC in WebRTC may still decode it from
// fMP4, so this one failure is not upstream of the transport.
const CODEC_MISMATCH: string = 'codecs not matched';

// Browser side only: go2rtc's own ice_servers stays empty because the VPS
// already knows its public address. The browser instead needs a server-reflexive
// candidate of its own, or Firefox fails ICE outright.
const ICE_SERVERS: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] }
];

// WHEP-style signaling: the complete offer is posted to the relay, which proxies
// it to go2rtc and returns the answer in the same response. Media then flows
// browser <-> go2rtc directly on 8555, decoded in hardware from a <video>.
export class WebRtcRenderer extends VideoElementRenderer {
  private peer: RTCPeerConnection | null = null;
  private readonly session = new AbortController();

  constructor(video: HTMLVideoElement, host: VideoRendererHost, private readonly signalingUrl: string) {
    super(video, host);
  }

  public start(): void {
    this.video.muted = true; // the live stream is video-only; also unblocks autoplay
    this.video.playsInline = true;
    this.observeVideo();

    // Everything negotiate() throws is a signaling failure, which means go2rtc
    // could not open the camera: MSE would reach the same camera the same way.
    // Except a codec mismatch, where the camera is fine and only WebRTC is not.
    this.negotiate().catch((error: unknown) => {
      if (this.session.signal.aborted) {
        return;
      }
      const reason: string = error instanceof Error ? error.message : 'signaling failed';
      this.host.onFatal(reason, !reason.includes(CODEC_MISMATCH));
    });
  }

  private async negotiate(): Promise<void> {
    const peer = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    this.peer = peer;

    peer.addTransceiver('video', { direction: 'recvonly' });

    peer.ontrack = (event: RTCTrackEvent): void => {
      this.video.srcObject = event.streams[0] ?? new MediaStream([event.track]);
      this.play();
    };

    // Only `failed` is final: `disconnected` is usually packet loss that ICE
    // recovers from, and the stall watchdog catches it when it doesn't.
    peer.onconnectionstatechange = (): void => {
      if (peer.connectionState === 'failed') {
        this.host.onFatal('peer connection failed');
      }
    };

    await peer.setLocalDescription(await peer.createOffer());
    await this.waitForIceGathering(peer);

    if (this.session.signal.aborted) {
      return;
    }

    const response: Response = await fetch(this.signalingUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: peer.localDescription?.sdp ?? '',
      signal: this.session.signal
    });

    if (!response.ok) {
      const reason: string = (await response.text()).trim();
      throw new Error(`signaling HTTP ${response.status}${reason ? `: ${reason}` : ''}`);
    }

    const sdp: string = await response.text();

    if (this.session.signal.aborted) {
      return;
    }

    await peer.setRemoteDescription({ type: 'answer', sdp });
  }

  // Non-trickle: the offer has to carry every candidate, so gathering must end
  // before it is posted.
  private waitForIceGathering(peer: RTCPeerConnection): Promise<void> {
    if (peer.iceGatheringState === 'complete') {
      return Promise.resolve();
    }

    return new Promise<void>(resolve => {
      const finish = (): void => {
        clearTimeout(timer);
        peer.removeEventListener('icegatheringstatechange', onStateChange);
        resolve();
      };
      const onStateChange = (): void => {
        if (peer.iceGatheringState === 'complete') {
          finish();
        }
      };
      const timer: ReturnType<typeof setTimeout> = setTimeout(finish, ICE_GATHERING_TIMEOUT_MS);
      peer.addEventListener('icegatheringstatechange', onStateChange);
    });
  }

  public override destroy(): void {
    // Abort first: close() fires connectionstatechange, which would otherwise
    // report a fatal for a teardown we asked for.
    this.session.abort();

    if (this.peer) {
      this.peer.ontrack = null;
      this.peer.onconnectionstatechange = null;
      try { this.peer.close(); } catch { /* already closed */ }
      this.peer = null;
    }

    super.destroy();
  }
}
